'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import { NetworkAvatar as Network, Select } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui';
import { Chevron } from '@repo/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { timeFrameOptions } from '@/config';
import { DashboardClass } from '@/contexts/Dashboard/Dashboard.context';
import { useWeb3Context } from '@/contexts/Web3Context/Web3.context';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import type { CardDashboardState } from '../CardDashboard.types';
import { CardDashboardChart } from '../CardDashboardChart';
import DashboardSizeButton from './DashboardSizeButton';
export interface CardDashboardHeaderProps {
  title: string;
  titleExtra?: React.ReactNode;
  totalSlot: React.ReactNode;
  state: CardDashboardState;
  isFilterDisabled: boolean;
  hideSizeButton?: boolean;
}
const CardDashboardHeader = t0 => {
  const $ = _c(43);
  const {
    title,
    titleExtra,
    state,
    isFilterDisabled,
    totalSlot,
    hideSizeButton
  } = t0;
  const {
    targetChainId
  } = useWeb3Context();
  const isSelectVisible = state.isSmall || state.isExpanded;
  const isChartVisible = !isSelectVisible;
  const {
    positionHistory: isPositionHistoryEnabled
  } = useFeatureFlags();
  let t1;
  if ($[0] !== targetChainId || $[1] !== titleExtra) {
    t1 = !titleExtra && <Network chainId={targetChainId} srcAvatarProps={{
      size: "small"
    }} />;
    $[0] = targetChainId;
    $[1] = titleExtra;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = title && <span className="text-secondary-foreground text-xs" style={{
      lineHeight: "normal"
    }}>{title}</span>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== state.mode || $[6] !== state.modeLabel || $[7] !== state.modeOptions || $[8] !== state.setMode) {
    t3 = state.setMode && state.modeOptions && state.mode && <><div className="md:hidden"><Select options={state.modeOptions} value={state.mode} onChange={state.setMode} triggerClassName={DashboardClass.PREVENT_DASHBOARD_CARD_HOVER_CLASS} /></div><div className="max-md:hidden"><Tabs value={state.mode} onValueChange={state.setMode} className={DashboardClass.PREVENT_DASHBOARD_CARD_HOVER_CLASS}><SegmentedControlList size="default" aria-label={state.modeLabel ?? "Chart view"}>{state.modeOptions.map(_temp)}</SegmentedControlList></Tabs></div></>;
    $[5] = state.mode;
    $[6] = state.modeLabel;
    $[7] = state.modeOptions;
    $[8] = state.setMode;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== titleExtra) {
    t4 = titleExtra && <><Chevron style={{
        transform: "rotate(-90deg)",
        flexShrink: 0
      }} color="var(--color-muted-foreground)" />{titleExtra}</>;
    $[10] = titleExtra;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== hideSizeButton || $[13] !== state.isExpanded || $[14] !== state.isHovered || $[15] !== state.isSmall) {
    t5 = !hideSizeButton && <AnimatePresence mode="wait">{!state.isSmall && <DashboardSizeButton isExpanded={state.isExpanded} isHovered={state.isHovered} />}</AnimatePresence>;
    $[12] = hideSizeButton;
    $[13] = state.isExpanded;
    $[14] = state.isHovered;
    $[15] = state.isSmall;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = <div className="gap-xs flex items-center">{t1}{t2}{t3}{t4}{t5}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t6 || $[24] !== totalSlot) {
    t7 = <div className="gap-xs flex flex-col">{t6}{totalSlot}</div>;
    $[23] = t6;
    $[24] = totalSlot;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isFilterDisabled || $[27] !== isSelectVisible || $[28] !== state.setTimeFrame || $[29] !== state.timeFrame) {
    t8 = isSelectVisible && <motion.div key="card-dashboard-select" className="gap-sm flex flex-wrap items-center justify-end" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }}><Select options={timeFrameOptions} value={state.timeFrame} onChange={state.setTimeFrame} disabled={isFilterDisabled} triggerClassName={DashboardClass.PREVENT_DASHBOARD_CARD_HOVER_CLASS} /></motion.div>;
    $[26] = isFilterDisabled;
    $[27] = isSelectVisible;
    $[28] = state.setTimeFrame;
    $[29] = state.timeFrame;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== isChartVisible || $[32] !== isPositionHistoryEnabled || $[33] !== state.data || $[34] !== state.error || $[35] !== state.isLoading) {
    t9 = isChartVisible && <div key="card-dashboard-mini-chart" className={cn("max-lg:hidden h-[91px] w-full max-w-[252px] overflow-hidden rounded-[20px]", isPositionHistoryEnabled && "border-[0.5px] border-border")}><CardDashboardChart data={state.data} statuses={{
        loading: state.isLoading,
        error: state.error
      }} variant="small" /></div>;
    $[31] = isChartVisible;
    $[32] = isPositionHistoryEnabled;
    $[33] = state.data;
    $[34] = state.error;
    $[35] = state.isLoading;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t8 || $[38] !== t9) {
    t10 = <AnimatePresence mode="wait" initial={false}>{t8}{t9}</AnimatePresence>;
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t7) {
    t11 = <div className="gap-sm flex flex-row justify-between max-lg:items-start">{t7}{t10}</div>;
    $[40] = t10;
    $[41] = t7;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
};
export default CardDashboardHeader;
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}